import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../ui/Icon';

const MENU_WIDTH = 176;
const GAP = 4;

/**
 * ⋮ row menu. Rendered in a portal with fixed positioning so the table's horizontal
 * scroll container can't clip it. Keyboard: Enter/Space/↓ opens and focuses the first
 * item, ↑/↓/Home/End move, Esc closes and returns focus to the ⋮ button, Tab closes.
 *
 * items: [{ label, onSelect, danger?, disabled?, hint? }]
 */
export default function RowActionsMenu({ label, items }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);
  const menuId = useId();

  const close = useCallback((returnFocus) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  // Place below the button, or above it when there isn't room
  useLayoutEffect(() => {
    if (!open) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const height = menuRef.current?.offsetHeight ?? 120;
    const below = rect.bottom + GAP;
    const top = below + height > window.innerHeight ? Math.max(GAP, rect.top - height - GAP) : below;
    const left = Math.min(Math.max(GAP, rect.right - MENU_WIDTH), window.innerWidth - MENU_WIDTH - GAP);
    setPosition({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open || !position) return;
    itemRefs.current[0]?.focus();
  }, [open, position]);

  // Click outside, scroll or resize closes the menu
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!menuRef.current?.contains(e.target) && !buttonRef.current?.contains(e.target)) close(false);
    };
    const onViewportChange = () => close(false);
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [open, close]);

  const onButtonKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onMenuKeyDown = (e) => {
    const nodes = itemRefs.current.filter(Boolean);
    const index = nodes.indexOf(document.activeElement);
    const focusAt = (i) => nodes[(i + nodes.length) % nodes.length]?.focus();
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusAt(index + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusAt(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusAt(0);
        break;
      case 'End':
        e.preventDefault();
        focusAt(nodes.length - 1);
        break;
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
      case 'Tab':
        close(false);
        break;
      default:
    }
  };

  const select = (item) => {
    if (item.disabled) return;
    close(false);
    item.onSelect();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={onButtonKeyDown}
        className="p-1 rounded-lg text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-colors outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
      >
        <Icon name="more_vert" size={18} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={label}
            onKeyDown={onMenuKeyDown}
            style={{ top: position?.top ?? -9999, left: position?.left ?? -9999, width: MENU_WIDTH }}
            className="fixed z-50 bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-[0_10px_15px_-3px_rgba(15,23,42,0.08),0_4px_6px_-4px_rgba(15,23,42,0.04)] p-1.5"
          >
            {items.map((item, i) => (
              <button
                key={item.label}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                aria-disabled={item.disabled || undefined}
                title={item.hint}
                onClick={() => select(item)}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot ${
                  item.danger ? 'text-error hover:bg-error-container/30 focus:bg-error-container/30' : 'text-on-surface hover:bg-surface-container focus:bg-surface-container'
                } ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
